import {Component, EventEmitter, Input, OnInit, Output} from "@angular/core";

// How many cards a row may hold, chosen by the person looking at them.
//
// The number is a CEILING, not a fixed count. The grid it drives refuses to make
// a card narrower than a readable minimum, so a narrow window quietly uses fewer
// columns while a wide one uses the full choice with the cards zoomed to fill it
// - which is the point on a monitor wider than full HD, where a fixed 250px card
// left most of the screen empty.
//
// The same idea as the video library of the new platform; done here with plain
// CSS grid, because this cabinet has no Tailwind to spell it out in classes.
// Reading the stored choice is a plain function, not an event.
//
// Emitting the restored value from ngOnInit would make the page that hosts this
// control depend on template order - the binding it feeds has to come later in
// the template, or Angular checks it before the value arrives (and says so, on
// every dev build). A page that needs the value at startup asks for it directly.
export function readGridDensity(storageKey: string,
                                options: number[] = [3, 4, 5, 6],
                                fallback: number = 4): number {
  try {
    const stored = Number(localStorage.getItem(storageKey))
    return options.indexOf(stored) !== -1 ? stored : fallback
  } catch(e){
    // Private windows and blocked site data throw on the read itself. A
    // remembered preference is never worth failing a page over.
    return fallback
  }
}

@Component({
  selector: "app-grid-density-control",
  templateUrl: "./grid-density-control.component.html",
  styleUrls: ["./grid-density-control.component.scss"],
})
export class GridDensityControlComponent implements OnInit {

  // Videos, streams and playlists are browsed differently, so each list
  // remembers its own density.
  @Input() storageKey: string = "livestream:grid:cols"
  @Input() options: number[] = [3, 4, 5, 6]
  @Input() fallback: number = 4

  @Output() changed = new EventEmitter<number>()

  cols: number = 4

  ngOnInit(){
    // Only the switch position is restored here. The value itself is read by
    // whoever needs it, through readGridDensity, so nothing is emitted into a
    // view that is already being checked.
    this.cols = readGridDensity(this.storageKey, this.options, this.fallback)
  }

  select(value: number){
    if(this.options.indexOf(value) === -1){ return }
    this.cols = value
    this.write(value)
    this.changed.emit(value)
  }

  private write(value: number){
    try { localStorage.setItem(this.storageKey, String(value)) } catch(e){}
  }
}
